import type { AvatarTone } from "./avatarPalettes";
import type { SeededRandom } from "./seededRandom";

/**
 * The wave patterns of generated avatars (F14), drawn in a 32 × 32 box whose centre the
 * disc is. Strokes are at least 1.8 units wide, so they stay readable at 24 px.
 */
export type AvatarShape =
  | { readonly kind: "line"; readonly tone: AvatarTone; readonly width: number; readonly d: string }
  | { readonly kind: "area"; readonly tone: AvatarTone; readonly d: string };

export type AvatarPatternName = "stripes" | "ripples" | "dunes" | "weave" | "ribbon";

export interface AvatarPattern {
  readonly name: AvatarPatternName;
  /** The range of turns, in degrees, the whole pattern is drawn at. */
  readonly turn: readonly [number, number];
  draw(random: SeededRandom): readonly AvatarShape[];
}

type Point = readonly [number, number];

const CENTRE = 16;
const TAU = Math.PI * 2;

const tenth = (value: number): number => Math.round(value * 10) / 10;

function pathOf(points: readonly Point[], closed: boolean): string {
  const steps = points.map(([x, y], index) => `${index === 0 ? "M" : "L"}${tenth(x)} ${tenth(y)}`);
  return steps.join("") + (closed ? "Z" : "");
}

/** A sine wave across the box (and past its edges, so turned patterns still fill the disc). */
function wave(random: SeededRandom, baseline: number, amplitude: readonly [number, number]): Point[] {
  const cycles = random.between(0.5, 1.6);
  const height = random.between(...amplitude);
  const phase = random.between(0, TAU);
  const points: Point[] = [];
  for (let x = -10; x <= 42; x += 2) {
    points.push([x, baseline + height * Math.sin((x / 32) * cycles * TAU + phase)]);
  }
  return points;
}

function turned(points: readonly Point[], degrees: number): Point[] {
  const angle = (degrees * Math.PI) / 180;
  const [cos, sin] = [Math.cos(angle), Math.sin(angle)];
  return points.map(([x, y]) => [CENTRE + (x - CENTRE) * cos - (y - CENTRE) * sin, CENTRE + (x - CENTRE) * sin + (y - CENTRE) * cos]);
}

/** Parallel waves, as in Zebratlas's avatars, in two alternating tones. */
const stripes: AvatarPattern = {
  name: "stripes",
  turn: [-60, 60],
  draw(random) {
    const count = random.pick([3, 4]);
    const tones = random.pick<readonly AvatarTone[]>([["pale", "deep"], ["deep", "pale"], ["pale", "soft"], ["soft", "deep"]]);
    const width = tenth(random.between(2.6, 3.8));
    const gap = 34 / count;
    return Array.from({ length: count }, (_, index) => ({
      kind: "line",
      tone: tones[index % tones.length] ?? "pale",
      width,
      d: pathOf(wave(random, -1 + gap * (index + 0.5) + random.between(-1, 1), [1.2, 2.8]), false),
    }));
  },
};

/** Rings around an off-centre point, each wobbling as a wave, like a drop in water. */
const ripples: AvatarPattern = {
  name: "ripples",
  turn: [0, 360],
  draw(random) {
    const [centreX, centreY] = [CENTRE + random.between(-7, 7), CENTRE + random.between(-7, 7)];
    const spacing = random.between(4.6, 6);
    const lobes = random.pick([3, 4, 5, 6]);
    const wobble = random.between(0.5, 1.3);
    const twist = random.between(0.2, 0.8);
    const tones = random.pick<readonly AvatarTone[]>([["pale", "deep"], ["deep", "pale"], ["pale", "soft"]]);
    const width = tenth(random.between(2.2, 2.8));
    const shapes: AvatarShape[] = [];
    for (let ring = 1; spacing * ring < 27; ring += 1) {
      const points: Point[] = [];
      for (let step = 0; step < 48; step += 1) {
        const angle = (step / 48) * TAU;
        const radius = spacing * ring + wobble * Math.sin(lobes * angle + twist * ring);
        points.push([centreX + radius * Math.cos(angle), centreY + radius * Math.sin(angle)]);
      }
      shapes.push({ kind: "line", tone: tones[ring % tones.length] ?? "pale", width, d: pathOf(points, true) });
    }
    return shapes;
  },
};

/** Three filled waves, one over the other, light at the back and dark in front. */
const dunes: AvatarPattern = {
  name: "dunes",
  turn: [-30, 30],
  draw(random) {
    const tones: readonly AvatarTone[] = ["pale", "soft", "deep"];
    const start = random.between(7, 11);
    const gap = random.between(6, 7.5);
    return tones.map((tone, index) => ({
      kind: "area",
      tone,
      d: pathOf([...wave(random, start + gap * index, [1.5, 3.4]), [42, 46], [-10, 46]], true),
    }));
  },
};

/** Two sets of thin waves crossing each other. */
const weave: AvatarPattern = {
  name: "weave",
  turn: [0, 180],
  draw(random) {
    const crossing = random.between(65, 115);
    const width = tenth(random.between(1.8, 2.3));
    const set = (tone: AvatarTone, degrees: number): AvatarShape[] =>
      [0, 1, 2].map((index) => ({
        kind: "line",
        tone,
        width,
        d: pathOf(turned(wave(random, 5.5 + index * 10.5, [1, 2.2]), degrees), false),
      }));
    const [back, front] = random.pick<readonly [AvatarTone, AvatarTone]>([["pale", "deep"], ["deep", "pale"], ["soft", "deep"]]);
    return [...set(back, 0), ...set(front, crossing)];
  },
};

/**
 * One closed Lissajous curve, whole inside the disc so it reads as a knot, never as a
 * letter: a broad band with a fine line along its middle.
 */
const ribbon: AvatarPattern = {
  name: "ribbon",
  turn: [0, 180],
  draw(random) {
    const [across, down] = random.pick<readonly [number, number]>([[2, 3], [3, 2]]);
    const [width, height] = [random.between(9, 10.5), random.between(9, 10.5)];
    // One frequency odd, one even: near a shift of 0 the curve is open, near π/2 it folds
    // back on itself into a single stroke.
    const shift = random.between(-0.25, 0.25);
    const points: Point[] = [];
    for (let step = 0; step < 120; step += 1) {
      const time = (step / 120) * TAU;
      points.push([CENTRE + width * Math.sin(across * time + shift), CENTRE + height * Math.sin(down * time)]);
    }
    const d = pathOf(points, true);
    const band = random.pick<AvatarTone>(["soft", "pale"]);
    return [
      { kind: "line", tone: band, width: tenth(random.between(3.2, 3.8)), d },
      { kind: "line", tone: "deep", width: 1.8, d },
    ];
  },
};

export const AVATAR_PATTERNS: readonly AvatarPattern[] = [stripes, ripples, dunes, weave, ribbon];
